import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Spinner, Alert, Form, Button } from "react-bootstrap";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import "../components/SchoolAdmin.css";
import {
    FaSync,
    FaDownload,
    FaEye,
    FaEnvelope,
    FaPhone,
    FaLink,
    FaBook,
    FaTags,
    FaSearch,
    FaTimes,
    FaCheckCircle,
    FaCircle
} from "react-icons/fa";

import {
    whoami,
    getCountries,
    getOnboardingPipelineOverview,
    getOnboardingSchoolsByStage,
    getPipelineOverview,
    getSchoolsByStage
} from "../services/APIClient";

// Stage configuration matching your OnboardingEvent model
const ONBOARDING_STAGES = [
    { key: 'trial_form_submitted', label: 'Trial Form Submitted', color: '#3b82f6', order: 1 },
    { key: 'loo_sent', label: 'LOO Sent', color: '#8b5cf6', order: 2 },
    { key: 'loo_signed', label: 'LOO Signed', color: '#ec4899', order: 3 },
    { key: 'unique_url_sent', label: 'Unique URL Sent', color: '#f59e0b', order: 4 },
    { key: 'al_signup_completed', label: 'Account Created', color: '#10b981', order: 5 },
    { key: 'trial_started', label: 'Trial Started', color: '#06b6d4', order: 6 },
    { key: 'class_created', label: 'Class Created', color: '#84cc16', order: 7 },
    { key: 'student_added', label: 'Student Added', color: '#f97316', order: 8 },
    { key: 'screener_assigned', label: 'Screener Assigned', color: '#ef4444', order: 9 },
    { key: 'screener_completed', label: 'Screener Completed', color: '#6366f1', order: 10 },
    { key: 'purchase_completed', label: 'Purchase Completed', color: '#14b8a6', order: 11 }
];

function OnboardingTrackerDash() {
    const [teacherid, setTeacherid] = useState(101);
    const [name, setName] = useState("John Doe");
    const [loading, setLoading] = useState(false);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [error, setError] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [isPortrait, setIsPortrait] = useState(
        window.matchMedia("(orientation: portrait)").matches
    );

    // Configuration options
    const [countries, setCountries] = useState([]);

    // Configuration parameters
    const [config, setConfig] = useState({
        countryId: "",
        academicYear: "25-26",
        onboardingStartDate: "2025-08-01",
        stageFilter: "" // Empty means all stages
    });

    // Dashboard data
    const [pipelineData, setPipelineData] = useState(null);
    const [viewMode, setViewMode] = useState('snapshot'); // 'snapshot' or 'funnel'
    const [expandedStages, setExpandedStages] = useState({});
    const [stageSchools, setStageSchools] = useState({});

    // Search functionality
    const [searchRollNumber, setSearchRollNumber] = useState("");
    const [searchResult, setSearchResult] = useState(null);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoadingConfig(true);
        try {
            // Load user info
            const userResponse = await whoami();
            console.log("User info response:", userResponse);
            setTeacherid(userResponse.data.stakeholder.id);
            setName(userResponse.data.stakeholder.name);

            // Load countries
            const countriesResponse = await getCountries();
            console.log("Countries loaded:", countriesResponse);
            setCountries(countriesResponse);

            // Find Ireland and set it as default
            const irelandCountry = countriesResponse.find(
                c => c.name?.toLowerCase() === 'ireland' ||
                    c.name?.toLowerCase() === 'republic of ireland' ||
                    c.code?.toLowerCase() === 'ie'
            );

            if (irelandCountry) {
                setConfig(prev => ({
                    ...prev,
                    countryId: irelandCountry.id
                }));
            }

            setLoadingConfig(false);
        } catch (err) {
            console.error("Error loading initial data:", err);
            setError("Failed to load configuration options.");
            setLoadingConfig(false);
        }
    };

    const loadDashboardData = async () => {
        if (!config.countryId) {
            setError("Country selection is required.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            console.log("Loading dashboard data with params:", config);

            // Load pipeline overview data
            const pipelineResponse = await getPipelineOverview(
                config.countryId,
                {
                    academicYear: config.academicYear,
                    onboardingStartDate: config.onboardingStartDate,
                    stageFilter: config.stageFilter
                }
            );

            console.log("Pipeline data:", pipelineResponse);
            setPipelineData(pipelineResponse);

            // Load school lists for each stage
            const stageSchoolsData = {};
            for (const stage of ONBOARDING_STAGES) {
                try {
                    const schoolsResponse = await getSchoolsByStage(
                        config.countryId,
                        stage.key,
                        {
                            academicYear: config.academicYear,
                            onboardingStartDate: config.onboardingStartDate
                        }
                    );
                    stageSchoolsData[stage.key] = schoolsResponse || [];
                } catch (err) {
                    console.error(`Error loading schools for stage ${stage.key}:`, err);
                    stageSchoolsData[stage.key] = [];
                }
            }

            setStageSchools(stageSchoolsData);
            setDataLoaded(true);
            setLoading(false);
        } catch (err) {
            console.error("Error loading dashboard data:", err);
            setError("Failed to load dashboard data. Please check your parameters and try again.");
            setLoading(false);
        }
    };

    const handleConfigChange = (field, value) => {
        console.log(`Config change: ${field} = ${value}`);
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        if (!searchRollNumber.trim()) {
            setSearchResult(null);
            return;
        }

        const rollNumberLower = searchRollNumber.trim().toLowerCase();

        // Search through all stages
        for (const stage of ONBOARDING_STAGES) {
            const schools = stageSchools[stage.key] || [];
            const foundSchool = schools.find(
                school => school.rollNumber?.toLowerCase() === rollNumberLower
            );

            if (foundSchool) {
                // Get the school's journey through all stages
                const journey = ONBOARDING_STAGES.map(s => {
                    const schoolsInStage = stageSchools[s.key] || [];
                    const wasInStage = schoolsInStage.some(
                        school => school.rollNumber?.toLowerCase() === rollNumberLower
                    );
                    return {
                        ...s,
                        completed: wasInStage || s.order < stage.order,
                        current: s.key === stage.key
                    };
                });

                setSearchResult({
                    school: foundSchool,
                    currentStage: stage,
                    journey: journey
                });
                return;
            }
        }

        // Not found
        setSearchResult({ notFound: true });
    };

    const clearSearch = () => {
        setSearchRollNumber("");
        setSearchResult(null);
    };

    const getCountryName = (countryId) => {
        const country = countries.find(c => c.id === countryId);
        return country?.name || 'Unknown';
    };

    const toggleStageExpansion = (stageKey) => {
        setExpandedStages(prev => ({
            ...prev,
            [stageKey]: !prev[stageKey]
        }));
    };

    const calculatePercentage = (count, total) => {
        if (!total || total === 0) return 0;
        return Math.round((count / total) * 100);
    };

    const getTimeBadgeClass = (daysStuck) => {
        if (daysStuck >= 10) return 'urgent';
        if (daysStuck >= 5) return 'warning';
        return 'normal';
    };

    const formatIrishDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        
        const date = new Date(dateString);
        
        // Format to Irish locale (en-IE) with Dublin timezone
        const options = {
            timeZone: 'Europe/Dublin',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        };
        
        return date.toLocaleString('en-IE', options);
    };

    const exportStageData = (stageKey) => {
        const schools = stageSchools[stageKey] || [];
        const stage = ONBOARDING_STAGES.find(s => s.key === stageKey);

        // Simple CSV export
        const headers = ['School Name', 'Roll Number', 'Principal Name', 'Principal Email', 'Alpaca Lead', 'Last Event', 'Days at Stage'];
        const csvData = schools.map(school => [
            school.schoolName,
            school.rollNumber,
            school.principalName || '',
            school.contactEmail || '',
            school.alpacaLeadEmail || '',
            school.lastEventDate || '',
            school.daysAtStage || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${stage.label.replace(/\s+/g, '_')}_schools.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const SearchResultsCard = () => {
        if (!searchResult) return null;

        if (searchResult.notFound) {
            return (
                <Alert variant="warning" className="mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <strong>No school found</strong> with roll number "{searchRollNumber}"
                        </div>
                        <Button variant="outline-warning" size="sm" onClick={clearSearch}>
                            <FaTimes className="me-1" /> Clear Search
                        </Button>
                    </div>
                </Alert>
            );
        }

        const { school, currentStage, journey } = searchResult;

        return (
            <Card className="class-admin-card mb-4">
                <Card.Header className="bg-white border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold">
                            <FaSearch className="me-2" />
                            Search Result: {school.rollNumber}
                        </h5>
                        <Button variant="outline-dark" size="sm" onClick={clearSearch}>
                            <FaTimes className="me-1" /> Clear Search
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {/* School Info */}
                    <Row className="mb-4">
                        <Col md={6}>
                            <h4 className="fw-bold mb-3">{school.schoolName}</h4>
                            <div className="mb-2">
                                <span className="text-muted">Roll Number:</span>{' '}
                                <strong>{school.rollNumber}</strong>
                            </div>
                            <div className="mb-2">
                                <span className="text-muted">Principal:</span>{' '}
                                <strong>{school.principalName || 'N/A'}</strong>
                            </div>
                            <div className="mb-2">
                                <span className="text-muted">Principal Email:</span>{' '}
                                <strong>{school.contactEmail || 'N/A'}</strong>
                            </div>
                            <div className="mb-2">
                                <span className="text-muted">Alpaca Lead:</span>{' '}
                                <strong>{school.alpacaLeadEmail || 'N/A'}</strong>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="p-4 bg-light border rounded">
                                <h6 className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.875rem' }}>
                                    Current Stage
                                </h6>
                                <div className="d-flex align-items-center mb-3">
                                    <div
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            backgroundColor: currentStage.color,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '18px',
                                            marginRight: '15px'
                                        }}
                                    >
                                        {currentStage.order}
                                    </div>
                                    <div>
                                        <div className="fw-bold">{currentStage.label}</div>
                                        <small className="text-muted">Stage {currentStage.order} of {ONBOARDING_STAGES.length}</small>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <span className="text-muted">Last Event:</span>{' '}
                                    <strong>{formatIrishDateTime(school.lastEventDate)}</strong>
                                </div>
                                <Badge
                                    bg={
                                        getTimeBadgeClass(school.daysAtStage) === 'urgent' ? 'dark' :
                                            getTimeBadgeClass(school.daysAtStage) === 'warning' ? 'secondary' :
                                                'light'
                                    }
                                    text={getTimeBadgeClass(school.daysAtStage) === 'normal' ? 'dark' : 'white'}
                                >
                                    {school.daysAtStage} days at this stage
                                </Badge>
                            </div>
                        </Col>
                    </Row>

                    {/* Journey Timeline */}
                    <div>
                        <h6 className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            Onboarding Journey
                        </h6>
                        <div className="position-relative">
                            {journey.map((stage, idx) => (
                                <div key={stage.key} className="d-flex align-items-start mb-3">
                                    <div className="d-flex flex-column align-items-center" style={{ width: '40px' }}>
                                        {stage.current ? (
                                            <div
                                                style={{
                                                    width: '30px',
                                                    height: '30px',
                                                    backgroundColor: stage.color,
                                                    border: '3px solid #000',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <FaCircle style={{ color: 'white', fontSize: '10px' }} />
                                            </div>
                                        ) : stage.completed ? (
                                            <FaCheckCircle
                                                style={{
                                                    fontSize: '30px',
                                                    color: stage.color
                                                }}
                                            />
                                        ) : (
                                            <FaCircle
                                                style={{
                                                    fontSize: '30px',
                                                    color: '#e5e7eb'
                                                }}
                                            />
                                        )}
                                        {idx < journey.length - 1 && (
                                            <div
                                                style={{
                                                    width: '2px',
                                                    height: '30px',
                                                    backgroundColor: stage.completed ? stage.color : '#e5e7eb',
                                                    marginTop: '5px',
                                                    marginBottom: '5px'
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="ms-3 flex-grow-1">
                                        <div className={`fw-bold ${stage.current ? 'text-dark' : stage.completed ? 'text-dark' : 'text-muted'}`}>
                                            {stage.label}
                                        </div>
                                        {stage.current && (
                                            <Badge bg="primary" className="mt-1">Current Stage</Badge>
                                        )}
                                        {!stage.current && stage.completed && (
                                            <small className="text-success">✓ Completed</small>
                                        )}
                                        {!stage.current && !stage.completed && (
                                            <small className="text-muted">Not reached</small>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex gap-2 flex-wrap mt-4 pt-3 border-top">
                        <Button variant="outline-dark" size="sm">
                            <FaEye className="me-1" /> View Full Details
                        </Button>
                        <Button variant="outline-dark" size="sm">
                            <FaEnvelope className="me-1" /> Email Contact
                        </Button>
                        {(currentStage.key === 'trial_form_submitted' || currentStage.key === 'loo_sent') && (
                            <Button variant="dark" size="sm">
                                <FaEnvelope className="me-1" /> Send {currentStage.key === 'loo_sent' ? 'Reminder' : 'LOO'}
                            </Button>
                        )}
                        {currentStage.key === 'loo_signed' && (
                            <Button variant="dark" size="sm">
                                <FaLink className="me-1" /> Send URL
                            </Button>
                        )}
                        {currentStage.key === 'al_signup_completed' && (
                            <Button variant="dark" size="sm">
                                <FaBook className="me-1" /> Send Guide
                            </Button>
                        )}
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const PipelineChart = () => {
        if (!pipelineData) return null;

        const stageData = viewMode === 'snapshot'
            ? pipelineData.currentSnapshot
            : pipelineData.funnelView;

        const totalSchools = pipelineData.totalSchools || 0;

        return (
            <div>
                <div className="d-flex justify-content-center gap-3 mb-3 flex-wrap">
                    {ONBOARDING_STAGES.map(stage => (
                        <div key={stage.key} className="d-flex align-items-center gap-2" style={{ fontSize: '11px' }}>
                            <div
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: stage.color,
                                    border: '1px solid #000'
                                }}
                            />
                            <span className="fw-bold">{stage.label}</span>
                        </div>
                    ))}
                </div>

                <div className="d-flex" style={{ height: '120px', border: '2px solid #000' }}>
                    {ONBOARDING_STAGES.map((stage, idx) => {
                        const count = stageData?.[stage.key] || 0;
                        const percentage = calculatePercentage(count, totalSchools);

                        return (
                            <div
                                key={stage.key}
                                style={{
                                    width: `${percentage}%`,
                                    backgroundColor: stage.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '13px',
                                    borderRight: idx < ONBOARDING_STAGES.length - 1 ? '1px solid rgba(0,0,0,0.2)' : 'none',
                                    cursor: 'pointer',
                                    transition: 'opacity 0.3s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                onClick={() => toggleStageExpansion(stage.key)}
                            >
                                {percentage > 0 && `${count} (${percentage}%)`}
                            </div>
                        );
                    })}
                </div>

                {/* Summary Stats */}
                <Row className="g-3 mt-3">
                    <Col md={3}>
                        <div className="p-3 bg-light border-2 border border-dark text-center">
                            <div className="small text-uppercase fw-bold mb-1">Total Schools</div>
                            <div className="h4 mb-0 fw-bold">{totalSchools}</div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="p-3 bg-light border-2 border border-dark text-center">
                            <div className="small text-uppercase fw-bold mb-1">Converted</div>
                            <div className="h4 mb-0 fw-bold">{pipelineData.converted || 0}</div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="p-3 bg-light border-2 border border-dark text-center">
                            <div className="small text-uppercase fw-bold mb-1">Conversion Rate</div>
                            <div className="h4 mb-0 fw-bold">{pipelineData.conversionRate || 0}%</div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="p-3 bg-light border-2 border border-dark text-center">
                            <div className="small text-uppercase fw-bold mb-1">Avg. Days to Purchase</div>
                            <div className="h4 mb-0 fw-bold">{pipelineData.avgDaysToPurchase || 0}</div>
                        </div>
                    </Col>
                </Row>
            </div>
        );
    };

    const StageCard = ({ stage }) => {
        const isExpanded = expandedStages[stage.key];
        const schools = stageSchools[stage.key] || [];
        const schoolCount = schools.length;

        return (
            <Card className="class-admin-card h-100">
                <Card.Header className="bg-white border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center flex-grow-1">
                            <div
                                className="me-2"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: stage.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                }}
                            >
                                {stage.order}
                            </div>
                            <div>
                                <h6 className="mb-0 fw-bold">{stage.label}</h6>
                                <small className="text-muted">{schoolCount} schools</small>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            {schoolCount > 0 && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => exportStageData(stage.key)}
                                >
                                    <FaDownload />
                                </Button>
                            )}
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => toggleStageExpansion(stage.key)}
                                className="text-decoration-none"
                            >
                                {isExpanded ? 'Hide' : `View ${schoolCount}`}
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                {isExpanded && (
                    <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {schools.length === 0 ? (
                            <div className="text-center text-muted py-3">
                                No schools at this stage
                            </div>
                        ) : (
                            <div>
                                {schools.map((school, idx) => (
                                    <div
                                        key={school.rollNumber || idx}
                                        className="pb-3 mb-3"
                                        style={{
                                            borderBottom: idx === schools.length - 1 ? 'none' : '2px dashed #dee2e6'
                                        }}
                                    >
                                        <h6 className="mb-2 fw-bold">{school.schoolName}</h6>
                                        <Row className="g-2 mb-2" style={{ fontSize: '12px' }}>
                                            <Col xs={6}>
                                                <span className="text-muted">Roll:</span>{' '}
                                                <strong>{school.rollNumber}</strong>
                                            </Col>
                                            <Col xs={6}>
                                                <span className="text-muted">Principal:</span>{' '}
                                                <strong>{school.principalName || 'N/A'}</strong>
                                            </Col>
                                            <Col xs={6}>
                                                <span className="text-muted">Principal Email:</span>{' '}
                                                <strong>{school.contactEmail || 'N/A'}</strong>
                                            </Col>
                                            <Col xs={6}>
                                                <span className="text-muted">Alpaca Lead:</span>{' '}
                                                <strong>{school.alpacaLeadEmail || 'N/A'}</strong>
                                            </Col>
                                            <Col xs={12}>
                                                <span className="text-muted">Last Event:</span>{' '}
                                                <strong>{formatIrishDateTime(school.lastEventDate)}</strong>
                                            </Col>
                                        </Row>

                                        {school.daysAtStage !== undefined && (
                                            <Badge
                                                bg={
                                                    getTimeBadgeClass(school.daysAtStage) === 'urgent' ? 'dark' :
                                                        getTimeBadgeClass(school.daysAtStage) === 'warning' ? 'secondary' :
                                                            'light'
                                                }
                                                text={getTimeBadgeClass(school.daysAtStage) === 'normal' ? 'dark' : 'white'}
                                                className="mb-2"
                                            >
                                                {school.daysAtStage} days at this stage
                                            </Badge>
                                        )}

                                        <div className="d-flex gap-2 flex-wrap mt-2">
                                            <Button variant="outline-dark" size="sm">
                                                <FaEye className="me-1" /> View Details
                                            </Button>
                                            <Button variant="outline-dark" size="sm">
                                                <FaEnvelope className="me-1" /> Email
                                            </Button>
                                            {(stage.key === 'trial_form_submitted' || stage.key === 'loo_sent') && (
                                                <Button variant="dark" size="sm">
                                                    <FaEnvelope className="me-1" /> Send {stage.key === 'loo_sent' ? 'Reminder' : 'LOO'}
                                                </Button>
                                            )}
                                            {stage.key === 'loo_signed' && (
                                                <Button variant="dark" size="sm">
                                                    <FaLink className="me-1" /> Send URL
                                                </Button>
                                            )}
                                            {stage.key === 'al_signup_completed' && (
                                                <Button variant="dark" size="sm">
                                                    <FaBook className="me-1" /> Send Guide
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card.Body>
                )}
            </Card>
        );
    };

    if (loadingConfig) {
        return (
            <div>
                <Container fluid className="class-admin-container">
                    <HeadingBanner
                        name={`Welcome ${name}!`}
                        isLoggedIn={true}
                        cloud={true}
                        teacherid={teacherid}
                    />
                    <Row className="class-admin-row">
                        <LeftPanel />
                        <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">
                            <div className="text-center py-5">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                                <p className="mt-3">Loading configuration...</p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div>
            <Container fluid className="class-admin-container">
                <HeadingBanner
                    name={`Welcome ${name}!`}
                    isLoggedIn={true}
                    cloud={true}
                    teacherid={teacherid}
                />

                <Row className="class-admin-row">
                    <LeftPanel />
                    <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

                        <div className="class-admin-header">
                            <h2 className="class-admin-page-title">Customer Onboarding Journey Tracker</h2>
                            <p className="class-admin-page-subtitle">Monitor customer onboarding progress, identify bottlenecks, and prioritize outreach</p>
                        </div>

                        {/* Configuration Panel */}
                        <Card className="class-admin-card mb-4">
                            <Card.Header className="bg-white">
                                <h6 className="mb-0 fw-bold">Dashboard Configuration</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">
                                                Country <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={config.countryId}
                                                onChange={(e) => handleConfigChange('countryId', e.target.value)}
                                                disabled={true}
                                            >
                                                <option value="">Select Country</option>
                                                {countries.map(country => (
                                                    <option key={country.id} value={country.id}>
                                                        {country.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Fixed to Republic of Ireland
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Academic Year</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={config.academicYear}
                                                onChange={(e) => handleConfigChange('academicYear', e.target.value)}
                                                placeholder="25-26"
                                            />
                                            <Form.Text className="text-muted">
                                                School year for filtering
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Onboarding Start Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={config.onboardingStartDate}
                                                onChange={(e) => handleConfigChange('onboardingStartDate', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                Filter schools from this date
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Current Stage Filter</Form.Label>
                                            <Form.Select
                                                value={config.stageFilter}
                                                onChange={(e) => handleConfigChange('stageFilter', e.target.value)}
                                            >
                                                <option value="">All Stages</option>
                                                {ONBOARDING_STAGES.map(stage => (
                                                    <option key={stage.key} value={stage.key}>
                                                        {stage.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Filter by current stage
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={9}>
                                        <Button
                                            variant="primary"
                                            onClick={loadDashboardData}
                                            disabled={loading || !config.countryId}
                                            className="w-100"
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Loading Data...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSync className="me-2" />
                                                    Load Dashboard Data
                                                </>
                                            )}
                                        </Button>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">Search by Roll Number</Form.Label>
                                            <div className="d-flex gap-2">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter roll number"
                                                    value={searchRollNumber}
                                                    onChange={(e) => setSearchRollNumber(e.target.value)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleSearch();
                                                        }
                                                    }}
                                                    disabled={!dataLoaded}
                                                />
                                                <Button
                                                    variant="secondary"
                                                    onClick={handleSearch}
                                                    disabled={!dataLoaded || !searchRollNumber.trim()}
                                                >
                                                    <FaSearch />
                                                </Button>
                                            </div>
                                            <Form.Text className="text-muted">
                                                {!dataLoaded ? 'Load data first to search' : 'Press Enter or click search'}
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {/* Search Results */}
                        {searchResult && <SearchResultsCard />}

                        {dataLoaded && pipelineData && !searchResult && (
                            <>
                                {/* Info Badge */}
                                <Alert variant="info" className="mb-4">
                                    <strong>Viewing data for:</strong> {getCountryName(config.countryId)} •
                                    <strong> Academic Year:</strong> {config.academicYear} •
                                    <strong> Onboarding since:</strong> {config.onboardingStartDate}
                                </Alert>

                                {/* Pipeline Overview */}
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="text-uppercase fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                            Onboarding Pipeline Overview
                                        </h5>
                                        <div className="btn-group" role="group">
                                            <Button
                                                variant={viewMode === 'snapshot' ? 'dark' : 'outline-dark'}
                                                size="sm"
                                                onClick={() => setViewMode('snapshot')}
                                            >
                                                Current Snapshot
                                            </Button>
                                            <Button
                                                variant={viewMode === 'funnel' ? 'dark' : 'outline-dark'}
                                                size="sm"
                                                onClick={() => setViewMode('funnel')}
                                            >
                                                Funnel View
                                            </Button>
                                        </div>
                                    </div>

                                    <Card className="class-admin-card">
                                        <Card.Body>
                                            <PipelineChart />
                                        </Card.Body>
                                    </Card>
                                </div>

                                {/* Stage Details */}
                                <div className="mb-4">
                                    <h5 className="text-uppercase fw-bold mb-3" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                        Detailed School Information by Stage
                                    </h5>
                                    <Row className="g-3">
                                        {ONBOARDING_STAGES.map(stage => (
                                            <Col key={stage.key} lg={6} className="mb-3">
                                                <StageCard stage={stage} />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </>
                        )}

                        {!dataLoaded && !loading && (
                            <Alert variant="info">
                                <Alert.Heading>Welcome to Customer Onboarding Journey Tracker</Alert.Heading>
                                <p>Please configure the parameters above and click "Load Dashboard Data" to view onboarding metrics.</p>
                                <hr />
                                <p className="mb-0 small">
                                    <strong>Note:</strong> Track schools through their entire onboarding journey from trial form submission to purchase completion.
                                </p>
                            </Alert>
                        )}

                    </Col>
                </Row>
            </Container>

            {isPortrait && <OrientationPrompt />}
        </div>
    );
}

export default OnboardingTrackerDash;